import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, UserX, ServerCog, BookCheck, Check } from 'lucide-react';
import AiDialog from './AiDialog';

const POINTS = [
  {
    icon: UserX,
    tile: 'bg-pop text-navy',
    title: 'Remove patient identifiers before pasting',
    text: 'Names, dates, MRNs, phone numbers, addresses and any other identifiers. Paste de-identified text only.',
  },
  {
    icon: ServerCog,
    tile: 'bg-primary text-primary-ink',
    title: 'De-identified again on our server',
    text: 'Text is de-identified on our server before it is sent to our AI provider. We never store your note text.',
  },
  {
    icon: BookCheck,
    tile: 'bg-sun text-sun-ink',
    title: 'Verify every suggestion',
    text: 'AI suggestions must be verified against the ICD-10-CM Tabular List and Official Guidelines.',
  },
];

/**
 * First-use healthcare notice for hosted AI. `onAccept()` stores consent; `onCancel()` aborts the request.
 */
export default function AiConsentDialog({ open, onAccept, onCancel }) {
  const [checked, setChecked] = useState(false);
  const checkRef = useRef(null);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  return (
    <AiDialog open={open} onClose={onCancel} labelledBy="ai-consent-title" describedBy="ai-consent-desc" initialFocusRef={checkRef} closeLabel="Cancel">
      <div className="border-b border-line/15 bg-navy px-5 pb-5 pt-6 text-on-navy sm:px-6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sun px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-sun-ink">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Healthcare data notice
        </span>
        <h2 id="ai-consent-title" className="mt-3 pr-8 font-display text-2xl font-extrabold leading-tight text-on-navy">
          Before you use AI
        </h2>
        <p id="ai-consent-desc" className="mt-1.5 text-sm text-on-navy/80">
          ICD Code Finder AI is included with your plan. Please confirm how you will use it. We ask once on this browser.
        </p>
      </div>

      <div className="px-5 py-5 sm:px-6">
        <ul className="space-y-3">
          {POINTS.map((p) => (
            <li key={p.title} className="flex items-start gap-3">
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${p.tile}`}>
                <p.icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="font-display text-[15px] font-bold leading-snug text-ink">{p.title}</p>
                <p className="mt-0.5 text-sm text-muted">{p.text}</p>
              </div>
            </li>
          ))}
        </ul>

        <label
          htmlFor="ai-consent-check"
          className={`mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border p-3.5 text-sm transition-colors ${checked ? 'border-ok/40 bg-ok-soft' : 'border-line/30 bg-surface-2 hover:border-primary/40'}`}
        >
          <span className="relative mt-0.5 grid h-5 w-5 shrink-0 place-items-center">
            <input
              id="ai-consent-check"
              ref={checkRef}
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-line/60 bg-surface transition-colors checked:border-ok checked:bg-ok focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            />
            <Check className="pointer-events-none absolute h-3.5 w-3.5 text-canvas opacity-0 peer-checked:opacity-100" strokeWidth={3.5} aria-hidden="true" />
          </span>
          <span className="font-medium text-ink">I will remove patient identifiers before pasting, and I understand AI suggestions must be verified by a qualified coder.</span>
        </label>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          <Link to="/privacy" className="text-sm font-semibold text-primary sm:mr-auto" onClick={onCancel}>
            How we handle AI data
          </Link>
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-pop" onClick={onAccept} disabled={!checked}>
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Agree and continue
          </button>
        </div>
      </div>
    </AiDialog>
  );
}
